import { gql } from '../typed-gql';
import {
    DIAGNOSIS_FRAGMENT,
    USER_FRAGMENT,
    CLIENT_USER_FRAGMENT,
    PROVIDER_USER_FRAGMENT,
    ORG_MEMBER_USER_FRAGMENT,
    APPOINTMENT_FRAGMENT,
    INSURANCE_CLAIM_FRAGMENT,
    RELEASE_FRAGMENT,
    PROVIDER_CARRIER_FRAGMENT,
    ASSESSMENT_FRAGMENT,
    PAYOUT_FRAGMENT,
    ORGANIZATION_FRAGMENT,
    ORGANIZATION_GROUP_FRAGMENT,
    MESSAGE_FRAGMENT,
    INSURANCE_CARRIER_FRAGMENT,
    JOURNAL_SUBMISSION_FRAGMENT,
    FAX_JOB_FRAGMENT,
    ASSIGNMENT_SUBMISSION_FRAGMENT,
    CLIENT_ASSIGNMENT_FRAGMENT,
    ASSIGNMENT_FRAGMENT,
    CONTACT_FRAGMENT,
    INVITE_GUEST_FRAGMENT,
    BETA_ACCESS_SETTINGS_FRAGMENT,
    SESSION_DATA_FRAGMENT,
    GROUP_PREVIEW_FRAGMENT,
    TOPIC_FRAGMENT,
    GROUP_MEMBER_FRAGMENT,
    GROUP_SESSION_FRAGMENT,
    GROUPS_SUBSCRIPTION_FRAGMENT,
    GROUP_PLATFORM_CONFIG_FRAGMENT,
    UPLOAD_URL_RESPONSE_FRAGMENT,
    GROUP_SESSION_RSVP_FRAGMENT,
    MESSAGING_CONVERSATION_FRAGMENT,
    ESCALATION_FRAGMENT,
    REFERRAL_CODE_FRAGMENT,
    CC_REFERRAL_PROGRESS_FRAGMENT,
    CALL_CONNECT_FRAGMENT,
    DOCUMENT_FRAGMENT,
    LICENSE_FRAGMENT,
    PROVIDER_CREDENTIALING_DATA_FRAGMENT,
    PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT,
    PROVIDER_FEEDBACK_LOG_DETAIL_FRAGMENT,
} from './fragments';

/*
 * Guest mutations
 */

export const LOGIN = gql`
    mutation Guest(
        $phoneNumber: String!
        $sendByEmail: Boolean
        $platform: String
        $isResend: Boolean!
    ) {
        login(
            phoneNumber: $phoneNumber
            sendByEmail: $sendByEmail
            platform: $platform
            isResend: $isResend
        ) {
            success
            isEmailVerified
        }
    }
`;

export const VERIFY_LOGIN_CODE = gql`
    ${USER_FRAGMENT}
    mutation Guest($phoneNumber: String!, $code: String!, $sentByEmail: Boolean, $options: JSON) {
        verifyLoginCode(
            phoneNumber: $phoneNumber
            code: $code
            sentByEmail: $sentByEmail
            options: $options
        ) {
            accessToken
            user {
                ...userFields
            }
        }
    }
`;

export const REGISTER = gql`
    mutation Guest(
        $firstName: String!
        $lastName: String!
        $phoneNumber: String!
        $email: String!
        $dateOfBirth: String!
        $source: String
        $fromMobile: Boolean
        $subscribeToNewsletter: Boolean
        $countryId: Int
        $referralCode: String
    ) {
        register(
            firstName: $firstName
            lastName: $lastName
            phoneNumber: $phoneNumber
            email: $email
            dateOfBirth: $dateOfBirth
            source: $source
            fromMobile: $fromMobile
            subscribeToNewsletter: $subscribeToNewsletter
            countryId: $countryId
            referralCode: $referralCode
        )
    }
`;

export const ACCEPT_APPOINTMENT_REQUEST = gql`
    mutation Guest($token: String!) {
        acceptAppointmentRequest(token: $token)
    }
`;

export const ACCEPT_APPOINTMENT_RESCHEDULE_REQUEST = gql`
    mutation Guest($token: String!) {
        acceptAppointmentRescheduleRequest(token: $token)
    }
`;

export const VERIFY_EMAIL = gql`
    mutation Guest($token: String!) {
        verifyEmailAddress(token: $token)
    }
`;

export const SIGN_MINOR_CONSENT = gql`
    mutation Guest($token: String!, $formData: MinorConsentSignatureInput!) {
        signMinorConsent(token: $token, formData: $formData)
    }
`;

/*
 * Users
 */

export const UPDATE_ADMIN = gql`
    mutation UpdateAdmin($userId: ID!, $update: AdminUpdate!) {
        updateAdmin(userId: $userId, update: $update)
    }
`;

export const UPDATE_CLIENT = gql`
    ${CLIENT_USER_FRAGMENT}

    mutation UpdateClient($clientUserId: ID!, $update: ClientUpdate!) {
        updateClient(clientUserId: $clientUserId, update: $update) {
            ...clientUserFields
        }
    }
`;

export const ACKNOWLEDGE_SUSPENSION = gql`
    mutation AcknowledgeSuspension {
        acknowledgeSuspension {
            userId
            suspensionReason
            suspensionDetails {
                reason
                suspendedAt
                acknowledgedAt
                appointmentId
            }
            isActive
        }
    }
`;

export const UPDATE_BETA_ACCESS = gql`
    ${BETA_ACCESS_SETTINGS_FRAGMENT}

    mutation UpdateBetaAccess($userId: ID!, $featureName: String!, $value: String!) {
        updateBetaAccess(userId: $userId, featureName: $featureName, value: $value) {
            ...betaAccessSettingsFields
        }
    }
`;

export const UPDATE_USER_METADATA = gql`
    mutation Guest($userId: ID!, $field: String!, $value: String!) {
        updateMetadata(userId: $userId, field: $field, value: $value)
    }
`;

export const UPDATE_CAREBOT = gql`
    mutation UpdateCarebot($clientUserId: ID!, $update: CarebotUpdate!) {
        updateCarebot(clientUserId: $clientUserId, update: $update)
    }
`;

export const SUBMIT_MEDICAL_INTAKE = gql`
    mutation SubmitIntake($intake: MedicalIntakeInput!, $isComplete: Boolean) {
        submitMedicalIntake(intake: $intake, isComplete: $isComplete)
    }
`;

/**
 * Staff variant: submits a client's intake on their behalf (write_client_intake,
 * only while it's incomplete). A separate document so the client path never
 * sends an argument older servers lack.
 */
export const SUBMIT_MEDICAL_INTAKE_FOR_CLIENT = gql`
    mutation SubmitIntakeForClient(
        $intake: MedicalIntakeInput!
        $isComplete: Boolean
        $clientUserId: ID!
    ) {
        submitMedicalIntake(intake: $intake, isComplete: $isComplete, clientUserId: $clientUserId)
    }
`;

export const SUBMIT_TREATMENT_PLAN = gql`
    mutation SubmitTreatmentPlan($clientUserId: ID!, $plan: TreatmentPlanInput!) {
        submitTreatmentPlan(clientUserId: $clientUserId, plan: $plan)
    }
`;

export const SUBMIT_SAFETY_PLAN = gql`
    mutation SubmitSafetyPlan($clientUserId: ID!, $plan: SafetyPlanInput!) {
        submitSafetyPlan(clientUserId: $clientUserId, plan: $plan)
    }
`;

export const SIGN_CLIENT_PLAN = gql`
    mutation SignClientPlan($clientPlanId: ID!) {
        signClientPlan(clientPlanId: $clientPlanId)
    }
`;

export const REGISTER_PROVIDER = gql`
    ${PROVIDER_USER_FRAGMENT}

    mutation Guest(
        $firstName: String!
        $lastName: String!
        $phoneNumber: String!
        $email: String!
        $dateOfBirth: String!
        $countryId: Int!
        $states: [String!]!
        $licenseNumber: String!
        $source: String
    ) {
        registerProvider(
            firstName: $firstName
            lastName: $lastName
            phoneNumber: $phoneNumber
            email: $email
            dateOfBirth: $dateOfBirth
            countryId: $countryId
            states: $states
            licenseNumber: $licenseNumber
            source: $source
        ) {
            ...providerUserFields
        }
    }
`;

export const UPDATE_PROVIDER = gql`
    ${PROVIDER_USER_FRAGMENT}

    mutation UpdateProvider($providerUserId: ID!, $update: ProviderUpdate!) {
        updateProvider(providerUserId: $providerUserId, update: $update) {
            ...providerUserFields
        }
    }
`;

export const UPDATE_PROVIDER_CREDENTIALING_DATA = gql`
    ${PROVIDER_CREDENTIALING_DATA_FRAGMENT}

    mutation UpdateProviderCredentialingData(
        $providerUserId: ID!
        $update: ProviderCredentialingDataUpdate!
    ) {
        updateProviderCredentialingData(providerUserId: $providerUserId, update: $update) {
            ...providerCredentialingDataFields
        }
    }
`;

/**
 * Signs the provider's credentialing consent.
 *
 * Records the typed name, date of birth, NPI and CAQH ID, and stamps the signing date and
 * IP server-side — neither is part of the input, since a self-reported one would be no
 * evidence of anything. `agreed` must be true.
 *
 * A provider holds one live consent at a time; signing while one stands is refused, and an
 * admin has to rescind it first (a separate screen).
 */
export const CREATE_CREDENTIALING_CONSENT = gql`
    ${PROVIDER_CREDENTIALING_DATA_FRAGMENT}

    mutation CreateCredentialingConsent(
        $providerUserId: ID!
        $consent: CredentialingConsentInput!
    ) {
        createCredentialingConsent(providerUserId: $providerUserId, consent: $consent) {
            ...providerCredentialingDataFields
        }
    }
`;

/**
 * Rescinds the provider's live credentialing consent. Admins with WRITE_CREDENTIALING_DATA
 * only — a provider who wants out asks for it in writing, per the authorisation's own terms.
 *
 * Files the signed form into the consent history and clears the consent fields, so the
 * provider can sign a new one. The uploaded copy of the form is unlinked, not deleted.
 */
export const RESCIND_CREDENTIALING_CONSENT = gql`
    ${PROVIDER_CREDENTIALING_DATA_FRAGMENT}

    mutation RescindCredentialingConsent($providerUserId: ID!) {
        rescindCredentialingConsent(providerUserId: $providerUserId) {
            ...providerCredentialingDataFields
        }
    }
`;

/*
 * Provider state licenses.
 *
 * Written one at a time rather than as a list on `updateProviderCredentialingData`, so
 * adding or editing one cannot disturb the others. Read back on
 * `providerCredentialingData.licenses` or `providerProfile.licensesV2`.
 *
 * A provider may only edit or delete a license within 3 days of adding it; after that it
 * is a record of what they attested to and an admin has to make the change. Adding is
 * never restricted.
 */

export const ADD_LICENSE = gql`
    ${LICENSE_FRAGMENT}
    mutation AddLicense($providerUserId: ID!, $license: ProviderLicenseCreate!) {
        addLicense(providerUserId: $providerUserId, license: $license) {
            ...licenseFields
        }
    }
`;

export const EDIT_LICENSE = gql`
    ${LICENSE_FRAGMENT}
    mutation EditLicense($providerUserId: ID!, $licenseId: ID!, $update: ProviderLicenseUpdate!) {
        editLicense(providerUserId: $providerUserId, licenseId: $licenseId, update: $update) {
            ...licenseFields
        }
    }
`;

export const DELETE_LICENSE = gql`
    mutation DeleteLicense($providerUserId: ID!, $licenseId: ID!) {
        deleteLicense(providerUserId: $providerUserId, licenseId: $licenseId)
    }
`;

export const UPDATE_PROVIDER_CARRIER = gql`
    ${PROVIDER_CARRIER_FRAGMENT}

    mutation UpdateProviderCarrier($providerUserId: ID!, $update: ProviderCarrierUpdate!) {
        updateProviderCarrier(providerUserId: $providerUserId, update: $update) {
            ...providerCarrierFields
        }
    }
`;

export const CREATE_PROVIDER = gql`
    ${PROVIDER_USER_FRAGMENT}

    mutation CreateProvider($provider: ProviderCreation!) {
        createProvider(provider: $provider) {
            ...providerUserFields
        }
    }
`;

export const CREATE_CLIENT = gql`
    ${CLIENT_USER_FRAGMENT}

    mutation CreateClient(
        $client: ClientCreation!
        $sendWelcomeNotification: Boolean!
        $source: String!
    ) {
        createClient(
            client: $client
            sendWelcomeNotification: $sendWelcomeNotification
            source: $source
        ) {
            ...clientUserFields
        }
    }
`;

export const SET_DEFAULT_PAYMENT_METHOD = gql`
    mutation SetDefaultPaymentMethod($clientUserId: ID!, $paymentMethodId: String!) {
        setDefaultPaymentMethod(clientUserId: $clientUserId, paymentMethodId: $paymentMethodId)
    }
`;

export const DELETE_PAYMENT_METHOD = gql`
    mutation DeletePaymentMethod($clientUserId: ID!, $paymentMethodId: String!) {
        deletePaymentMethod(clientUserId: $clientUserId, paymentMethodId: $paymentMethodId)
    }
`;

export const ISSUE_REFUND = gql`
    mutation IssueRefund($paymentId: ID!, $amount: Int!) {
        issueRefund(paymentId: $paymentId, amount: $amount)
    }
`;

export const SUBMIT_JOURNAL = gql`
    ${JOURNAL_SUBMISSION_FRAGMENT}
    mutation SubmitJournal($journal: JournalSubmissionInput!) {
        submitJournal(journal: $journal) {
            ...journalSubmissionFields
        }
    }
`;

export const GENERATE_JOURNAL_ENTRY = gql`
    mutation GenerateJournalEntry($payload: JournalGenerationInput!) {
        generateJournalEntry(payload: $payload) {
            body
            comment
        }
    }
`;

export const ACTIVATE_CLIENT_ACCESS = gql`
    mutation ActivateClientAccess($userId: ID!) {
        activateClientAccess(userId: $userId)
    }
`;

export const SWITCH_ACTING_ROLE = gql`
    mutation SwitchRole($roleName: String!) {
        switchRole(roleName: $roleName)
    }
`;

export const SEND_EMAIL_VERIFICATION = gql`
    mutation SendEmailVerification($userId: ID!) {
        sendEmailVerification(userId: $userId)
    }
`;

export const CHANGE_ROLE = gql`
    mutation ChangeRole($userId: ID!, $roleName: String!) {
        changeRole(userId: $userId, roleName: $roleName)
    }
`;

export const DELETE_USER = gql`
    mutation DeleteUser($userId: ID!) {
        deleteUser(userId: $userId)
    }
`;

export const RESYNC_STRIPE_ACCOUNT = gql`
    mutation ResyncStripeAccount($providerUserId: ID!) {
        resyncStripeAccount(providerUserId: $providerUserId)
    }
`;

export const GENERATE_STRIPE_ONBOARDING_LINK = gql`
    mutation GenerateStripeOnboardingLink($providerUserId: ID!) {
        generateStripeOnboardingLink(providerUserId: $providerUserId)
    }
`;

export const COMPLETE_PROVIDER_REGISTRATION = gql`
    mutation CompleteRegistration($providerUserId: ID!) {
        completeRegistration(providerUserId: $providerUserId)
    }
`;

export const UPDATE_LANGUAGE = gql`
    mutation UpdateLanguage($locale: String!) {
        updateLanguage(locale: $locale)
    }
`;

/*
 * Policies
 */

export const DELETE_POLICY = gql`
    mutation DeletePolicy($policyId: ID!) {
        deletePolicy(policyId: $policyId)
    }
`;

// Callers read no field from the response and always reload() (refetch
// GET_POLICIES), so only the normalization key is returned.
export const UPDATE_POLICY = gql`
    mutation UpdatePolicy($policyId: ID!, $update: PolicyUpdate!) {
        updatePolicy(policyId: $policyId, update: $update) {
            policyId
        }
    }
`;

export const CREATE_POLICY = gql`
    mutation CreatePolicy($clientUserId: ID!, $policy: PolicyCreation!) {
        createPolicy(clientUserId: $clientUserId, policy: $policy) {
            policyId
        }
    }
`;

/*
 * Minor Consent
 */

export const ISSUE_MINOR_CONSENT = gql`
    mutation IssueMinorConsent($clientUserId: ID!, $issue: MinorConsentIssueInput!) {
        issueMinorConsent(clientUserId: $clientUserId, issue: $issue)
    }
`;

/*
 * Releases
 */

export const ISSUE_RELEASE = gql`
    mutation IssueRelease($clientUserId: ID!, $details: ReleaseInput!) {
        issueRelease(clientUserId: $clientUserId, details: $details)
    }
`;

export const RESCIND_RELEASE = gql`
    ${RELEASE_FRAGMENT}
    mutation RescindRelease($releaseId: ID!) {
        rescindRelease(releaseId: $releaseId) {
            ...releaseFields
        }
    }
`;

/*
 * Documents
 */

export const DELETE_DOCUMENT = gql`
    mutation DeleteDocument($documentId: ID!) {
        deleteDocument(documentId: $documentId)
    }
`;

/**
 * Tags an uploaded document as one of a provider's credentialing files and claims it.
 *
 * A provider may hold any number of documents of each type, so this never replaces or
 * deletes another — which document currently fills a credentialing slot is set separately,
 * through `updateProviderCredentialingData`.
 *
 * The file format must suit the type: a headshot and a photo ID must be images, every
 * other type must not be.
 */
export const CLAIM_CREDENTIAL_DOCUMENT = gql`
    ${DOCUMENT_FRAGMENT}
    mutation ClaimCredentialDocument(
        $documentId: ID!
        $credentialDocumentType: CredentialDocumentType!
        $title: String
        $description: String
    ) {
        claimCredentialDocument(
            documentId: $documentId
            credentialDocumentType: $credentialDocumentType
            title: $title
            description: $description
        ) {
            ...documentFields
        }
    }
`;

/**
 * Metadata-only edit of an existing document (no new file).
 *
 * Backend contract expected by the edit dialog:
 *   input UpdateDocumentInput {
 *       documentId: ID!
 *       title: String
 *       description: String
 *       hideFromProvider: Boolean   # not sent yet — see note below
 *       hideFromClient: Boolean     # not sent yet — see note below
 *   }
 *   updateDocument(input: UpdateDocumentInput!): Document
 *
 * Only the uploader or an admin may update, matching `deleteDocument`. Omitted
 * fields must be left untouched: the client deliberately does not send the
 * visibility flags, because `Document` does not expose `restrictions` yet and so
 * the form cannot prefill them. Once `restrictions { hideFromProvider hideFromClient }`
 * is exposed on `Document`, the edit form can prefill and start sending them.
 *
 * Replacing the *file* does not go through here — that reuses
 * `requestDocumentUploadUrl` with a `documentId` in the input (see utils/upload.ts).
 */
export const UPDATE_DOCUMENT = gql`
    ${DOCUMENT_FRAGMENT}
    mutation UpdateDocument($input: UpdateDocumentInput!) {
        updateDocument(input: $input) {
            ...documentFields
        }
    }
`;

/*
 * Organizations
 */

export const UPDATE_ORGANIZATION = gql`
    ${ORGANIZATION_FRAGMENT}

    mutation UpdateOrganization($organizationId: ID!, $update: OrganizationUpdate!) {
        updateOrganization(organizationId: $organizationId, update: $update) {
            ...organizationFields
        }
    }
`;

export const CREATE_ORGANIZATION_MEMBER = gql`
    ${ORG_MEMBER_USER_FRAGMENT}

    mutation CreateOrganizationMember(
        $organizationId: ID!
        $client: ClientCreation!
        $sendWelcomeNotification: Boolean
        $departmentId: ID
        $teamId: ID
        $locationId: ID
    ) {
        createOrganizationMember(
            organizationId: $organizationId
            client: $client
            sendWelcomeNotification: $sendWelcomeNotification
            departmentId: $departmentId
            teamId: $teamId
            locationId: $locationId
        ) {
            ...orgMemberUserFields
        }
    }
`;

export const UPDATE_ORGANIZATION_MEMBER = gql`
    ${ORG_MEMBER_USER_FRAGMENT}

    mutation UpdateOrganizationMember($userId: ID!, $update: OrganizationMemberUpdate!) {
        updateOrganizationMember(userId: $userId, update: $update) {
            ...orgMemberUserFields
        }
    }
`;

export const CREATE_ORGANIZATION_GROUP = gql`
    ${ORGANIZATION_GROUP_FRAGMENT}

    mutation CreateOrganizationGroup(
        $organizationId: ID!
        $type: String!
        $payload: OrganizationGroupCreation!
    ) {
        createOrganizationGroup(organizationId: $organizationId, type: $type, payload: $payload) {
            ...organizationGroupFields
        }
    }
`;

export const UPDATE_ORGANIZATION_GROUP = gql`
    ${ORGANIZATION_GROUP_FRAGMENT}

    mutation UpdateOrganizationGroup($groupId: ID!, $update: OrganizationGroupUpdate!) {
        updateOrganizationGroup(groupId: $groupId, update: $update) {
            ...organizationGroupFields
        }
    }
`;

export const DELETE_ORGANIZATION_GROUP = gql`
    mutation DeleteOrganizationGroup($groupId: ID!) {
        deleteOrganizationGroup(groupId: $groupId)
    }
`;

export const MAKE_ORGANIZATION_PAYMENT = gql`
    mutation MakeOrganizationPayment(
        $organizationId: ID!
        $paymentId: ID!
        $method: String!
        $stripePaymentIntentId: String
    ) {
        makeOrganizationPayment(
            organizationId: $organizationId
            paymentId: $paymentId
            method: $method
            stripePaymentIntentId: $stripePaymentIntentId
        )
    }
`;

/*
 * Availability
 */

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const UPDATE_NORMAL_AVAILABILITY = gql`
    mutation UpdateNormalAvailability($providerUserId: ID!, $update: NormalAvailabilityUpdate!) {
        updateNormalAvailability(providerUserId: $providerUserId, update: $update)
    }
`;

export const UPDATE_SPECIFIC_AVAILABILITY = gql`
    mutation UpdateSpecificAvailability(
        $providerUserId: ID!
        $update: SpecificAvailabilityUpdate!
    ) {
        updateSpecificAvailability(providerUserId: $providerUserId, update: $update)
    }
`;

export const EVALATE_AVAILABILITY = gql`
    mutation EvaluateAvailability($providerUserId: ID!) {
        evaluateAvailability(providerUserId: $providerUserId)
    }
`;

export const REGISTER_COMPSYCH_LOCATION = gql`
    mutation RegisterComPsychLocation($providerUserId: ID!, $state: String!, $locationId: String!) {
        registerComPsychLocation(
            providerUserId: $providerUserId
            state: $state
            locationId: $locationId
        )
    }
`;

export const DELETE_COMPSYCH_LOCATION = gql`
    mutation DeleteComPsychLocation($providerUserId: ID!, $locationId: String!) {
        deleteComPsychLocation(providerUserId: $providerUserId, locationId: $locationId)
    }
`;

export const REJECT_AVAILABILITY_OFFER = gql`
    mutation RejectAvailabilityOffer($providerUserId: ID!) {
        rejectAvailabilityOffer(providerUserId: $providerUserId)
    }
`;

export const REVOKE_ACCOUNT_PROFILE = gql`
    mutation RevokeAccountProfile($profileId: String!) {
        revokeAccountProfile(profileId: $profileId)
    }
`;

/*
 * Appointments
 */

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const START_SESSION = gql`
    mutation StartSession($appointmentId: ID!, $platform: String) {
        startSession(appointmentId: $appointmentId, platform: $platform)
    }
`;

export const SESSION_LIVE_SESSION = gql`
    mutation StartLiveSession($appointmentId: ID!, $platform: String) {
        startLiveSession(appointmentId: $appointmentId, platform: $platform) {
            token
            videoCallBuild
            displayName
        }
    }
`;

export const BOOK_APPOINTMENT = gql`
    ${APPOINTMENT_FRAGMENT}

    mutation BookAppointment($details: AppointmentBooking!) {
        bookAppointment(details: $details) {
            ...appointmentFields
        }
    }
`;

export const CONFIRM_APPOINTMENT = gql`
    ${APPOINTMENT_FRAGMENT}

    mutation ConfirmAppointment($details: AppointmentConfirmation!) {
        confirmAppointment(details: $details) {
            ...appointmentFields
        }
    }
`;

export const CANCEL_APPOINTMENT = gql`
    ${APPOINTMENT_FRAGMENT}

    mutation CancelAppointment($details: AppointmentCancellation!) {
        cancelAppointment(details: $details) {
            ...appointmentFields
        }
    }
`;

export const RESCHEDULE_APPOINTMENT = gql`
    ${APPOINTMENT_FRAGMENT}

    mutation RescheduleAppointment($details: AppointmentRescheduling!) {
        rescheduleAppointment(details: $details) {
            ...appointmentFields
        }
    }
`;

export const OVERRIDE_APPOINTMENT = gql`
    ${APPOINTMENT_FRAGMENT}

    mutation OverrideAppointment($appointmentId: ID!, $status: String!) {
        overrideAppointment(appointmentId: $appointmentId, status: $status) {
            ...appointmentFields
        }
    }
`;

export const MARK_APPOINTMENT_OCCURRED = gql`
    ${APPOINTMENT_FRAGMENT}

    mutation MarkAppointmentOccurred($appointmentId: ID!) {
        markAppointmentOccurred(appointmentId: $appointmentId) {
            ...appointmentFields
        }
    }
`;

export const CHARGE_APPOINTMENT_FEE = gql`
    mutation ChargeAppointmentFee($appointmentId: ID!) {
        chargeAppointmentFee(appointmentId: $appointmentId)
    }
`;

export const CHARGE_CUSTOM_PAYMENT = gql`
    mutation ChargeCustomPayment($appointmentId: ID!, $paymentAmount: Int!) {
        chargeCustomPayment(appointmentId: $appointmentId, paymentAmount: $paymentAmount)
    }
`;

export const LOG_SESSION_EVENTS = gql`
    mutation LogSessionEvents($appointmentId: ID!, $sessionEvents: [SessionEventInput]!) {
        logSessionEvents(appointmentId: $appointmentId, sessionEvents: $sessionEvents)
    }
`;

export const CREATE_DEMO_APPOINTMENTS = gql`
    mutation CreateDemoAppointments {
        createDemoAppointments {
            selfPay {
                confirmedAppointment {
                    appointmentId
                }
            }
            eap {
                confirmedAppointment {
                    appointmentId
                }
            }
        }
    }
`;

/*
 * Assessments
 */

export const SUBMIT_ASSESSMENTS = gql`
    ${ASSESSMENT_FRAGMENT}
    mutation Guest($submission: AssessmentSubmission!, $token: String) {
        submitAssessments(submission: $submission, token: $token) {
            ...assessmentFields
        }
    }
`;

/*
 * Claims & Carriers
 */

export const UPDATE_INSURANCE_CLAIM = gql`
    ${INSURANCE_CLAIM_FRAGMENT}
    mutation UpdateInsuranceClaim(
        $insuranceClaimId: ID!
        $update: InsuranceClaimUpdate!
        $waiveSuspension: Boolean
    ) {
        updateInsuranceClaim(
            insuranceClaimId: $insuranceClaimId
            update: $update
            waiveSuspension: $waiveSuspension
        ) {
            ...insuranceClaimFields
        }
    }
`;

export const DELETE_INSURANCE_CLAIM = gql`
    mutation DeleteInsuranceClaim($insuranceClaimId: ID!) {
        deleteInsuranceClaim(insuranceClaimId: $insuranceClaimId)
    }
`;

export const SET_CLAIM_FLAG = gql`
    ${INSURANCE_CLAIM_FRAGMENT}
    mutation SetClaimFlag($insuranceClaimId: ID!, $flag: String) {
        setClaimFlag(insuranceClaimId: $insuranceClaimId, flag: $flag) {
            ...insuranceClaimFields
        }
    }
`;

export const UPDATE_INSURANCE_CARRIER = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    mutation UpdateInsuranceCarrier($insuranceCarrierId: ID!, $update: InsuranceCarrierUpdate!) {
        updateInsuranceCarrier(insuranceCarrierId: $insuranceCarrierId, update: $update) {
            ...insuranceCarrierFields
        }
    }
`;

export const SET_CARRIER_AUTO_PANEL = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    mutation SetCarrierAutoPanel(
        $insuranceCarrierId: ID!
        $enabled: Boolean!
        $unpanelProviderUserIds: [Int!]
    ) {
        setCarrierAutoPanel(
            insuranceCarrierId: $insuranceCarrierId
            enabled: $enabled
            unpanelProviderUserIds: $unpanelProviderUserIds
        ) {
            carrier {
                ...insuranceCarrierFields
            }
            paneledCount
            unpaneledCount
        }
    }
`;

export const APPLY_CARRIER_ROSTER = gql`
    mutation ApplyCarrierRoster(
        $insuranceCarrierId: ID!
        $panel: [RosterPanelInput!]!
        $unpanelUserIds: [Int!]!
    ) {
        applyCarrierRoster(
            insuranceCarrierId: $insuranceCarrierId
            panel: $panel
            unpanelUserIds: $unpanelUserIds
        ) {
            paneledCount
            unchangedCount
            unpaneledCount
        }
    }
`;

export const REMOVE_CARRIER_LOGO = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    mutation RemoveCarrierLogo($insuranceCarrierId: ID!, $variant: CarrierLogoVariant!) {
        removeCarrierLogo(insuranceCarrierId: $insuranceCarrierId, variant: $variant) {
            ...insuranceCarrierFields
        }
    }
`;

export const UPDATE_PAYER_CO_BRANDING = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    mutation UpdatePayerCoBranding($input: PayerCoBrandingInput!) {
        updatePayerCoBranding(input: $input) {
            ...insuranceCarrierFields
        }
    }
`;

export const CREATE_INSURANCE_CARRIER = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    mutation CreateInsuranceCarrier($payload: InsuranceCarrierCreation!) {
        createInsuranceCarrier(payload: $payload) {
            ...insuranceCarrierFields
        }
    }
`;

/*
 * Payments & Payouts
 */

export const ISSUE_APPOINTMENT_PAYOUTS = gql`
    mutation IssueAppointmentPayouts(
        $providerUserIds: [ID!]!
        $type: String!
        $ranges: AppointmentPayoutRangeMap!
        $amount: Int!
        $currency: String!
        $amountLimit: Int
    ) {
        issueAppointmentPayouts(
            providerUserIds: $providerUserIds
            type: $type
            ranges: $ranges
            amount: $amount
            currency: $currency
            amountLimit: $amountLimit
        ) {
            jobId
            queued
        }
    }
`;

export const ISSUE_CUSTOM_PAYOUT = gql`
    ${PAYOUT_FRAGMENT}
    mutation IssueCustomPayout(
        $providerUserId: ID!
        $amount: Int!
        $currency: String!
        $description: String
        $statementDescriptor: String
    ) {
        issueCustomPayout(
            providerUserId: $providerUserId
            amount: $amount
            currency: $currency
            description: $description
            statementDescriptor: $statementDescriptor
        ) {
            ...payoutFields
        }
    }
`;

export const RETRY_PAYOUT = gql`
    ${PAYOUT_FRAGMENT}
    mutation RetryPayout($payoutId: ID!) {
        retryPayout(payoutId: $payoutId) {
            ...payoutFields
        }
    }
`;

/*
 * Diagnoses
 */

export const SUBMIT_DX_CODES = gql`
    ${DIAGNOSIS_FRAGMENT}

    mutation SubmitCodes($appointmentId: ID!, $codes: [String]!) {
        submitCodes(appointmentId: $appointmentId, codes: $codes) {
            ...diagnosisFields
        }
    }
`;

export const SUBMIT_DX_NOTES = gql`
    ${DIAGNOSIS_FRAGMENT}

    mutation SubmitNotes($appointmentId: ID!, $notes: DiagnosisNotesInput!) {
        submitNotes(appointmentId: $appointmentId, notes: $notes) {
            ...diagnosisFields
        }
    }
`;

export const SIGN_DX_NOTES = gql`
    ${DIAGNOSIS_FRAGMENT}

    mutation SignNotes($appointmentId: ID!) {
        signNotes(appointmentId: $appointmentId) {
            ...diagnosisFields
        }
    }
`;

export const OVERRIDE_DX_NOTES = gql`
    ${DIAGNOSIS_FRAGMENT}

    mutation OverrideNotes($appointmentId: ID!) {
        overrideNotes(appointmentId: $appointmentId) {
            ...diagnosisFields
        }
    }
`;

export const RESTORE_AI_NOTES = gql`
    ${DIAGNOSIS_FRAGMENT}

    mutation RestoreAINotes($appointmentId: ID!) {
        restoreAiNotes(appointmentId: $appointmentId) {
            ...diagnosisFields
        }
    }
`;

export const UNLOCK_DX_CODES = gql`
    ${DIAGNOSIS_FRAGMENT}

    mutation UnlockCodes($appointmentId: ID!) {
        unlockCodes(appointmentId: $appointmentId) {
            ...diagnosisFields
        }
    }
`;

export const UNLOCK_DX_NOTES = gql`
    ${DIAGNOSIS_FRAGMENT}

    mutation UnlockNotes($appointmentId: ID!) {
        unlockNotes(appointmentId: $appointmentId) {
            ...diagnosisFields
        }
    }
`;

export const REPROCESS_DX_NOTES = gql`
    ${DIAGNOSIS_FRAGMENT}

    mutation ReprocessNotes($appointmentId: ID!) {
        reprocessAiNotes(appointmentId: $appointmentId) {
            ...diagnosisFields
        }
    }
`;

/*
 * Conversations
 */

export const POST_MESSAGE = gql`
    ${MESSAGE_FRAGMENT}

    mutation PostMessage(
        $userId: ID!
        $conversationName: String!
        $messageContent: String!
        $notifyUser: Boolean
        $asyncEval: Boolean
    ) {
        postMessage(
            userId: $userId
            conversationName: $conversationName
            messageContent: $messageContent
            notifyUser: $notifyUser
            asyncEval: $asyncEval
        ) {
            ...messageFields
        }
    }
`;

/*
 * Medical Fax
 */
export const START_MEDICAL_DOCUMENT_FAX_JOB = gql`
    ${FAX_JOB_FRAGMENT}
    mutation StartMedicalDocumentFaxJob(
        $recipientFax: String!
        $clientUserId: ID!
        $documents: [DocumentReference!]!
        $coverNote: String
    ) {
        startMedicalDocumentFaxJob(
            recipientFax: $recipientFax
            clientUserId: $clientUserId
            documents: $documents
            coverNote: $coverNote
        ) {
            ...faxJobFields
        }
    }
`;

/*
 * Assignments
 */

export const SUBMIT_ASSIGNMENT = gql`
    ${ASSIGNMENT_SUBMISSION_FRAGMENT}
    mutation SubmitAssignment(
        $slug: String!
        $content: JSON!
        $answers: [AssignmentAnswerInput!]!
        $withInference: Boolean = false
    ) {
        submitAssignment(slug: $slug, content: $content, answers: $answers) {
            ...assignmentSubmissionFields
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const UPDATE_ASSIGNMENT_SUBMISSION = gql`
    ${ASSIGNMENT_SUBMISSION_FRAGMENT}
    mutation UpdateAssignmentSubmission(
        $submissionId: ID!
        $content: JSON
        $answers: [AssignmentAnswerInput!]!
        $withInference: Boolean = false
    ) {
        updateAssignmentSubmission(
            submissionId: $submissionId
            content: $content
            answers: $answers
        ) {
            ...assignmentSubmissionFields
        }
    }
`;

export const ACCEPT_ASSIGNMENT_INFERENCE_GOAL = gql`
    mutation AcceptAssignmentInferenceGoal($suggestionId: ID!) {
        acceptAssignmentInferenceGoal(suggestionId: $suggestionId) {
            goalId
            userId
            journalId
            body
            type
            status
            visibility
            createdAt
            updatedAt
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const CREATE_ASSIGNMENT = gql`
    ${ASSIGNMENT_FRAGMENT}
    mutation CreateAssignment(
        $name: String!
        $description: String
        $pages: [AssignmentPageInput!]!
        $active: Boolean
    ) {
        createAssignment(name: $name, description: $description, pages: $pages, active: $active) {
            ...assignmentFields
        }
    }
`;

export const ASSIGN_ASSIGNMENT = gql`
    ${CLIENT_ASSIGNMENT_FRAGMENT}
    mutation AssignAssignment($slug: String!, $clientIds: [ID!]!, $dueDate: String!) {
        assignAssignment(slug: $slug, clientIds: $clientIds, dueDate: $dueDate) {
            ...clientAssignmentFields
        }
    }
`;

export const UNASSIGN_CLIENT_ASSIGNMENT = gql`
    ${CLIENT_ASSIGNMENT_FRAGMENT}
    mutation UnassignClientAssignment($clientAssignmentId: ID!) {
        unassignClientAssignment(clientAssignmentId: $clientAssignmentId) {
            ...clientAssignmentFields
        }
    }
`;

export const UPDATE_CLIENT_ASSIGNMENT_DUE_DATE = gql`
    ${CLIENT_ASSIGNMENT_FRAGMENT}
    mutation UpdateClientAssignmentDueDate($clientAssignmentId: ID!, $dueDate: String!) {
        updateClientAssignmentDueDate(clientAssignmentId: $clientAssignmentId, dueDate: $dueDate) {
            ...clientAssignmentFields
        }
    }
`;

// TrustedUsers
export const INVITE_TRUSTED_USER = gql`
    ${INVITE_GUEST_FRAGMENT}
    mutation InviteTrustedUser($guestInfo: InviteGuestInput!) {
        inviteTrustedUser(guestInfo: $guestInfo) {
            ...inviteGuestFields
        }
    }
`;

/**
 * Admin variant: invites on the client's behalf, so the contact, consent
 * release and care-coordination task belong to the client. A separate
 * document so the client path never sends an argument older servers lack.
 */
export const INVITE_TRUSTED_USER_FOR_CLIENT = gql`
    ${INVITE_GUEST_FRAGMENT}
    mutation InviteTrustedUserForClient($guestInfo: InviteGuestInput!, $clientUserId: ID!) {
        inviteTrustedUser(guestInfo: $guestInfo, clientUserId: $clientUserId) {
            ...inviteGuestFields
        }
    }
`;

export const ADD_TRUSTED_USER = gql`
    mutation AddTrustedUser($userId: ID!, $trustedUserId: ID!) {
        addTrustedUser(userId: $userId, trustedUserId: $trustedUserId) {
            userId
            trustedUserIds
        }
    }
`;

export const REMOVE_TRUSTED_USER = gql`
    mutation RemoveTrustedUser($userId: ID!, $trustedUserId: ID!) {
        removeTrustedUser(userId: $userId, trustedUserId: $trustedUserId) {
            userId
            trustedUserIds
        }
    }
`;

export const UPDATE_INVITED_USER = gql`
    mutation UpdateContact($contactId: ID!, $updateData: UpdateContactInput!) {
        updateContact(contactId: $contactId, updateData: $updateData) {
            contactId
            firstName
            lastName
            phoneNumber
            email
            relationship
            consentForContact
            isPending
            createdAt
            note
            attachToAppointment
        }
    }
`;

export const DELETE_CONTACT = gql`
    mutation DeleteContact($contactId: ID!) {
        deleteContact(contactId: $contactId)
    }
`;

/*
 * Appointments - Group Sessions
 */
export const ADD_GUESTS_TO_APPOINTMENT = gql`
    mutation AddGuestsToAppointment($appointmentId: ID!, $guestUserIds: [ID]!) {
        addGuestsToAppointment(appointmentId: $appointmentId, guestUserIds: $guestUserIds) {
            appointmentId
            guestIds
            guests {
                userId
                firstName
                lastName
            }
        }
    }
`;

export const REMOVE_GUESTS_FROM_APPOINTMENT = gql`
    mutation RemoveGuestsFromAppointment($appointmentId: ID!, $guestUserIds: [ID]!) {
        removeGuestsFromAppointment(appointmentId: $appointmentId, guestUserIds: $guestUserIds) {
            appointmentId
            guestIds
        }
    }
`;
/*
 * Uploads
 */

export const REQUEST_DOCUMENT_UPLOAD_URL = gql`
    ${UPLOAD_URL_RESPONSE_FRAGMENT}
    mutation RequestDocumentUploadUrl($input: DocumentUploadInput!) {
        requestDocumentUploadUrl(input: $input) {
            ...uploadUrlResponseFields
        }
    }
`;

export const REQUEST_ENTITY_FILE_UPLOAD_URL = gql`
    ${UPLOAD_URL_RESPONSE_FRAGMENT}
    mutation RequestEntityFileUploadUrl($input: EntityFileUploadInput!) {
        requestEntityFileUploadUrl(input: $input) {
            ...uploadUrlResponseFields
        }
    }
`;

/*
 * AI Prep Generation
 */

export const PROCESS_SESSION_INFERENCE = gql`
    ${SESSION_DATA_FRAGMENT}
    mutation ProcessSessionInference($appointmentId: ID!) {
        processSessionInference(appointmentId: $appointmentId) {
            ...sessionDataFields
        }
    }
`;

/*
 * Groups
 */

export const REQUEST_GROUP_APPROVAL = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    mutation RequestGroupApproval($input: RequestGroupApprovalInput!) {
        requestGroupApproval(input: $input) {
            ...groupPreviewFields
        }
    }
`;

export const CREATE_GROUP = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    mutation CreateGroup($input: CreateGroupInput!) {
        createGroup(input: $input) {
            ...groupPreviewFields
        }
    }
`;

export const CREATE_GROUP_DRAFT = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    mutation CreateGroupDraft($input: CreateGroupDraftInput!) {
        createGroupDraft(input: $input) {
            ...groupPreviewFields
        }
    }
`;

// Returns groupPreviewFields (not the heavy groupFields with groupMembers /
// completedSessions / historyLog lists): callers only read scalar/relation
// fields for form-reset and always refetchQueries GET_GROUP / PaginatedGroups.
export const UPDATE_GROUP = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    mutation UpdateGroup($input: UpdateGroupInput!) {
        updateGroup(input: $input) {
            ...groupPreviewFields
        }
    }
`;

export const CANCEL_GROUP_SUBMISSION = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    mutation CancelGroupSubmission($input: CancelGroupSubmissionInput!) {
        cancelGroupSubmission(input: $input) {
            ...groupPreviewFields
        }
    }
`;

export const DELETE_GROUP = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    mutation DeleteGroup($input: DeleteGroupInput!) {
        deleteGroup(input: $input) {
            ...groupPreviewFields
        }
    }
`;

export const CREATE_TOPIC = gql`
    ${TOPIC_FRAGMENT}
    mutation CreateTopic($input: CreateTopicInput!) {
        createTopic(input: $input) {
            ...topicFields
        }
    }
`;

export const UPDATE_TOPIC = gql`
    ${TOPIC_FRAGMENT}
    mutation UpdateTopic($input: UpdateTopicInput!) {
        updateTopic(input: $input) {
            ...topicFields
        }
    }
`;

export const DESTROY_TOPIC = gql`
    ${TOPIC_FRAGMENT}
    mutation DestroyTopic($input: DestroyTopicInput!) {
        destroyTopic(input: $input) {
            ...topicFields
        }
    }
`;

export const JOIN_GROUP_QUEUE = gql`
    ${GROUP_MEMBER_FRAGMENT}
    mutation JoinGroupQueue($groupId: Int!) {
        joinGroupQueue(groupId: $groupId) {
            ...groupMemberFields
        }
    }
`;

export const LEAVE_GROUP_QUEUE = gql`
    ${GROUP_MEMBER_FRAGMENT}
    mutation LeaveGroupQueue($groupId: Int!) {
        leaveGroupQueue(groupId: $groupId) {
            ...groupMemberFields
        }
    }
`;

export const RESCHEDULE_GROUP_SESSION = gql`
    ${GROUP_SESSION_FRAGMENT}
    mutation RescheduleGroupSession($input: RescheduleGroupSessionInput!) {
        rescheduleGroupSession(input: $input) {
            ...groupSessionFields
        }
    }
`;

export const CANCEL_GROUP_SESSION = gql`
    ${GROUP_SESSION_FRAGMENT}
    mutation CancelGroupSession($input: CancelGroupSessionInput!) {
        cancelGroupSession(input: $input) {
            ...groupSessionFields
        }
    }
`;

export const UPDATE_GROUP_STATUS = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    mutation UpdateGroupStatus($input: UpdateGroupStatusInput!) {
        updateGroupStatus(input: $input) {
            ...groupPreviewFields
        }
    }
`;

export const UPDATE_MULTIPLE_GROUPS_STATUS = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    mutation UpdateMultipleGroupsStatus($input: [UpdateGroupStatusInput!]!) {
        updateMultipleGroupsStatus(input: $input) {
            ...groupPreviewFields
        }
    }
`;

export const SUBSCRIBE_TO_GROUP_PLATFORM = gql`
    ${GROUPS_SUBSCRIPTION_FRAGMENT}
    mutation SubscribeToGroupPlatform($input: GroupSubscriptionInput!) {
        subscribeToGroupPlatform(input: $input) {
            ...groupsSubscriptionFields
        }
    }
`;

export const UNSUBSCRIBE_FROM_GROUP_PLATFORM = gql`
    ${GROUPS_SUBSCRIPTION_FRAGMENT}
    mutation UnsubscribeFromGroupPlatform($input: GroupSubscriptionInput!) {
        unsubscribeFromGroupPlatform(input: $input) {
            ...groupsSubscriptionFields
        }
    }
`;

export const CREATE_NEW_GROUP_PLATFORM_CONFIG = gql`
    ${GROUP_PLATFORM_CONFIG_FRAGMENT}
    mutation CreateNewGroupPlatformConfig($input: CreateNewGroupPlatformConfigInput!) {
        createNewGroupPlatformConfig(input: $input) {
            ...groupPlatformConfigFields
        }
    }
`;

export const MODERATE_GROUP_MEMBERSHIPS = gql`
    ${GROUP_MEMBER_FRAGMENT}
    mutation ModerateGroupMemberships($input: ModerateGroupMembershipsInput!) {
        moderateGroupMemberships(input: $input) {
            ...groupMemberFields
        }
    }
`;

export const JOIN_GROUP_QUEUE_V2 = gql`
    ${GROUP_MEMBER_FRAGMENT}
    mutation JoinGroupQueueV2($input: JoinGroupQueueInput!) {
        joinGroupQueueV2(input: $input) {
            ...groupMemberFields
        }
    }
`;

export const APPROVE_WAITLISTED_MEMBERS = gql`
    ${GROUP_MEMBER_FRAGMENT}
    mutation ApproveWaitlistedMembers($input: ApproveWaitlistedMembersInput!) {
        approveWaitlistedMembers(input: $input) {
            ...groupMemberFields
        }
    }
`;

export const GET_OR_CREATE_GROUP_SESSION_URL = gql`
    mutation GetOrCreateGroupSessionURL($groupSessionId: Int!) {
        getOrCreateSessionURL(groupSessionId: $groupSessionId) {
            groupSessionId
            roomUrl
            groupId
            startDate
            endDate
            status
            createdAt
            deletedAt
        }
    }
`;

export const RESPOND_TO_GROUP_SESSION = gql`
    ${GROUP_SESSION_RSVP_FRAGMENT}
    mutation RespondToGroupSession($input: RespondToGroupSessionInput!) {
        respondToGroupSession(input: $input) {
            ...groupSessionRsvpFields
        }
    }
`;

// These mutations only change recruiting fields; callers refetch the full
// provider (useUser.refetch). The response is scoped to the recruiting fields
// that provider-list tables (AdminProviders/PayerProviders) read from the
// normalized cache — keep recruitingStatus/assignedRecruiter or those labels
// go stale.
const RECRUITING_RESULT = `
    userId
    providerProfile {
        recruitingStatus
        pausedFromStatus
        contractStatus
        contractDocumentId
        assignedRecruiter {
            userId
            name
            photoUrl
        }
    }
`;

export const TRANSITION_RECRUITING_STATUS = gql`
    mutation TransitionRecruitingStatus($providerUserId: ID!, $status: String!, $reason: String) {
        transitionRecruitingStatus(
            providerUserId: $providerUserId
            status: $status
            reason: $reason
        ) {
            ${RECRUITING_RESULT}
        }
    }
`;

export const RESUME_RECRUITING = gql`
    mutation ResumeRecruiting($providerUserId: ID!) {
        resumeRecruiting(providerUserId: $providerUserId) {
            ${RECRUITING_RESULT}
        }
    }
`;

export const ASSIGN_RECRUITER = gql`
    mutation AssignRecruiter($providerUserId: ID!, $recruiterUserId: ID!) {
        assignRecruiter(providerUserId: $providerUserId, recruiterUserId: $recruiterUserId) {
            ${RECRUITING_RESULT}
        }
    }
`;

export const UNASSIGN_RECRUITER = gql`
    mutation UnassignRecruiter($providerUserId: ID!) {
        unassignRecruiter(providerUserId: $providerUserId) {
            ${RECRUITING_RESULT}
        }
    }
`;

export const PROVIDER_FEEDBACK_BOOKING_LINK = gql`
    mutation ProviderFeedbackBookingLink {
        providerFeedbackBookingLink
    }
`;

export const GENERATE_CALENDLY_LINK = gql`
    mutation GenerateCalendlyLink($clinicianUserId: ID!, $linkType: CalendlyLinkType!) {
        generateCalendlyLink(clinicianUserId: $clinicianUserId, linkType: $linkType)
    }
`;

/*
 * Messaging
 */

export const SEND_MESSAGE = gql`
    ${MESSAGING_CONVERSATION_FRAGMENT}
    mutation SendMessage($input: SendMessageInput!, $withConversationDetail: Boolean = false) {
        sendMessage(input: $input) {
            message {
                ...messagingMessageFields
            }
            conversation {
                ...messagingConversationFields
            }
            sideEffectMessages {
                ...messagingMessageFields
            }
        }
    }
`;

export const MARK_CONVERSATION_READ = gql`
    ${MESSAGING_CONVERSATION_FRAGMENT}
    mutation MarkConversationRead(
        $conversationId: Int!
        $upToMessageId: Int
        $withConversationDetail: Boolean = false
    ) {
        markConversationRead(conversationId: $conversationId, upToMessageId: $upToMessageId) {
            ...messagingConversationFields
        }
    }
`;

export const CREATE_ESCALATION = gql`
    ${ESCALATION_FRAGMENT}
    mutation CreateEscalation($input: CreateEscalationInput!) {
        createEscalation(input: $input) {
            ...escalationFields
        }
    }
`;

export const RESOLVE_ESCALATION = gql`
    ${ESCALATION_FRAGMENT}
    mutation ResolveEscalation($escalationId: Int!, $resolutionNote: String) {
        resolveEscalation(escalationId: $escalationId, resolutionNote: $resolutionNote) {
            ...escalationFields
        }
    }
`;

export const REOPEN_ESCALATION = gql`
    ${ESCALATION_FRAGMENT}
    mutation ReopenEscalation($escalationId: Int!, $reason: String) {
        reopenEscalation(escalationId: $escalationId, reason: $reason) {
            ...escalationFields
        }
    }
`;

export const SET_PROVIDER_MESSAGING_ENABLED = gql`
    mutation SetProviderMessagingEnabled($input: SetProviderMessagingEnabledInput!) {
        setProviderMessagingEnabled(input: $input) {
            messagingEnabled
        }
    }
`;

/*
 * Care Circle Referral
 */

export const GENERATE_REFERRAL_CODE = gql`
    ${REFERRAL_CODE_FRAGMENT}
    mutation GenerateReferralCode {
        generateReferralCode {
            ...referralCodeFields
        }
    }
`;

export const USE_REFERRAL_REWARD = gql`
    ${CC_REFERRAL_PROGRESS_FRAGMENT}
    mutation UseReferralReward($ccReferralProgressId: ID!) {
        useReferralReward(ccReferralProgressId: $ccReferralProgressId) {
            ...ccReferralProgressFields
        }
    }
`;

export const START_CALL_CONNECT = gql`
    ${CALL_CONNECT_FRAGMENT}
    mutation StartCallConnect($appointmentId: ID!) {
        startCallConnect(appointmentId: $appointmentId) {
            ...callConnectFields
        }
    }
`;

// Call-back from the missed-call SMS, authorised by the link's single-use token; must
// stay a `Guest` operation. The result deliberately says nothing about the call.
export const START_CALL_CONNECT_AS_GUEST = gql`
    mutation Guest($token: String!) {
        startCallConnectAsGuest(token: $token) {
            started
        }
    }
`;

export const CANCEL_CALL_CONNECT = gql`
    ${CALL_CONNECT_FRAGMENT}
    mutation CancelCallConnect($callConnectId: ID!) {
        cancelCallConnect(callConnectId: $callConnectId) {
            ...callConnectFields
        }
    }
`;

export const END_CALL_CONNECT = gql`
    ${CALL_CONNECT_FRAGMENT}
    mutation EndCallConnect($callConnectId: ID!) {
        endCallConnect(callConnectId: $callConnectId) {
            ...callConnectFields
        }
    }
`;

/*
 * In-app notifications
 */

/**
 * Marks all unseen in-app notifications as seen — clears the bell badge.
 * Does NOT affect per-item read state.
 */
export const MARK_NOTIFICATIONS_SEEN = gql`
    mutation MarkNotificationsSeen {
        markNotificationsSeen
    }
`;

/** Marks one or more in-app notifications as read for the current user. */
export const READ_NOTIFICATIONS = gql`
    mutation ReadNotif($notificationIds: [Int!]!) {
        readNotif(notificationIds: $notificationIds)
    }
`;

/*
 * Provider Feedback
 */

export const CREATE_PROVIDER_FEEDBACK_LOG = gql`
    ${PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT}
    mutation CreateProviderFeedbackLog($input: CreateProviderFeedbackLogInput!) {
        createProviderFeedbackLog(input: $input) {
            ...providerFeedbackLogListItemFields
        }
    }
`;

export const INVESTIGATE_PROVIDER_FEEDBACK_LOG = gql`
    ${PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT}
    mutation InvestigateProviderFeedbackLog(
        $providerFeedbackLogId: Int!
        $input: InvestigateProviderFeedbackLogInput!
    ) {
        investigateProviderFeedbackLog(
            providerFeedbackLogId: $providerFeedbackLogId
            input: $input
        ) {
            ...providerFeedbackLogListItemFields
        }
    }
`;

export const RESOLVE_PROVIDER_FEEDBACK_LOG = gql`
    ${PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT}
    mutation ResolveProviderFeedbackLog(
        $providerFeedbackLogId: Int!
        $input: ResolveProviderFeedbackLogInput!
    ) {
        resolveProviderFeedbackLog(providerFeedbackLogId: $providerFeedbackLogId, input: $input) {
            ...providerFeedbackLogListItemFields
        }
    }
`;

export const REOPEN_PROVIDER_FEEDBACK_LOG = gql`
    ${PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT}
    mutation ReopenProviderFeedbackLog($providerFeedbackLogId: Int!) {
        reopenProviderFeedbackLog(providerFeedbackLogId: $providerFeedbackLogId) {
            ...providerFeedbackLogListItemFields
        }
    }
`;

export const UPDATE_PROVIDER_FEEDBACK_LOG = gql`
    ${PROVIDER_FEEDBACK_LOG_DETAIL_FRAGMENT}
    mutation UpdateProviderFeedbackLog(
        $providerFeedbackLogId: Int!
        $input: UpdateProviderFeedbackLogInput!
    ) {
        updateProviderFeedbackLog(providerFeedbackLogId: $providerFeedbackLogId, input: $input) {
            ...providerFeedbackLogDetailFields
        }
    }
`;

export const REASSIGN_PROVIDER_FEEDBACK_LOG = gql`
    ${PROVIDER_FEEDBACK_LOG_DETAIL_FRAGMENT}
    mutation ReassignProviderFeedbackLog(
        $providerFeedbackLogId: Int!
        $input: ReassignProviderFeedbackLogInput!
    ) {
        reassignProviderFeedbackLog(providerFeedbackLogId: $providerFeedbackLogId, input: $input) {
            ...providerFeedbackLogDetailFields
        }
    }
`;

export const UPDATE_PROVIDER_FEEDBACK_INVESTIGATION_NOTES = gql`
    ${PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT}
    mutation UpdateProviderFeedbackInvestigationNotes(
        $providerFeedbackLogId: Int!
        $notes: String
    ) {
        updateProviderFeedbackInvestigationNotes(
            providerFeedbackLogId: $providerFeedbackLogId
            notes: $notes
        ) {
            ...providerFeedbackLogListItemFields
            investigationNotes
        }
    }
`;

export const ADD_PROVIDER_FEEDBACK_ACTION_ITEM = gql`
    ${PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT}
    mutation AddProviderFeedbackActionItem($providerFeedbackLogId: Int!, $text: String!) {
        addProviderFeedbackActionItem(providerFeedbackLogId: $providerFeedbackLogId, text: $text) {
            ...providerFeedbackLogListItemFields
            investigationNotes
        }
    }
`;

export const UPDATE_PROVIDER_FEEDBACK_ACTION_ITEM = gql`
    ${PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT}
    mutation UpdateProviderFeedbackActionItem($providerFeedbackActionItemId: Int!, $text: String!) {
        updateProviderFeedbackActionItem(
            providerFeedbackActionItemId: $providerFeedbackActionItemId
            text: $text
        ) {
            ...providerFeedbackLogListItemFields
            investigationNotes
        }
    }
`;

export const DELETE_PROVIDER_FEEDBACK_ACTION_ITEM = gql`
    ${PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT}
    mutation DeleteProviderFeedbackActionItem($providerFeedbackActionItemId: Int!) {
        deleteProviderFeedbackActionItem(
            providerFeedbackActionItemId: $providerFeedbackActionItemId
        ) {
            ...providerFeedbackLogListItemFields
            investigationNotes
        }
    }
`;

export const SET_ACTION_ITEM_COMPLETED = gql`
    mutation SetActionItemCompleted($itemId: Int!, $completed: Boolean!) {
        setActionItemCompleted(itemId: $itemId, completed: $completed) {
            id
            text
            completed
        }
    }
`;

export const SUBMIT_PROVIDER_FEEDBACK = gql`
    mutation SubmitProviderFeedback($input: SubmitProviderFeedbackInput!) {
        submitProviderFeedback(input: $input) {
            log {
                providerFeedbackLogId
                category
                status
            }
            sessionRef {
                appointmentId
                groupSessionId
                groupId
            }
        }
    }
`;
